import { Component, computed, inject, OnInit, Renderer2 } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Event, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { WsSymbolService } from 'core/+state';
import {
    AnalyticsService,
    AuthenticationService,
    AuthorizationService,
    NavigationService,
    Principal,
    SPHERE_ENVIRONMENT_PRODUCTION,
    SphereAppShellComponent,
    SphereCompanySettingsService,
    SystemSettingsService,
    UserSettingsActions,
    WsEventService
} from '@sphere/platform';
import { CookieNoticeComponent } from './portal/cookie-notice/cookie-notice.component';
import { ServiceWorkerUpdateService } from './portal/sw-update/sw-update.service';
import { patchState, signalState } from '@ngrx/signals';
import { StorageMap } from '@ngx-pwa/local-storage';
import { AgGridThemeService } from '@sphere/components';
import localforage from 'localforage';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { filter, take, tap } from 'rxjs/operators';
import { ResetUserPasswordService } from 'shared/dialogs/reset-user-password-dialog/reset-user-password.service';
import { EventHandlersService } from '@sphere/platform';
import { NotificationSubscriptionsService } from 'built-in-apps-shared';
import { SettingsFullscreenDialogService } from '@sphere/platform';
import { spherePlatformApps } from './__app-registration';
import { Store } from '@ngrx/store';

export type SphereUIVersion = 'v1' | 'v2';

export type SphereRootShellState = {
    collapsedSidenav: boolean;
    routerNavigationLoading: boolean;
    displayBottomBar: boolean;
    uiVersion: SphereUIVersion;
    isEnverusAuthentication: boolean;
    user: Principal;
    ready: boolean;
};

@Component({
    template: `
        @if (state.ready()) {
            <sphere-shell-app
                [hasSideNavigation]="hasSideNavigation()"
                [currentApp]="currentApp()"
                [isShell]="isShell()"
                [noPaddingRoute]="noPaddingRoute()"
                [routerNavigationLoading]="state().routerNavigationLoading"
                [username]="state.user()?.username"
                [canResetPassword]="state.isEnverusAuthentication()"
                (openSettingsDialog)="openGlobalSettings()"
                (openResetPasswordDialog)="openResetPasswordDialog()"
                (logout)="logout()"
            />
        } @else {
            <p>Loading...</p>
        }
    `,
    imports: [SphereAppShellComponent]
})
export class SphereAppComponent implements OnInit {
    private readonly authentication = inject(AuthenticationService);
    private readonly authorization = inject(AuthorizationService);
    private readonly navigationService = inject(NavigationService);
    private readonly bottomSheet = inject(MatBottomSheet);
    private readonly notificationSubscriptions = inject(NotificationSubscriptionsService);
    private readonly companySettings = inject(SphereCompanySettingsService);
    private readonly systemSettings = inject(SystemSettingsService);
    private readonly storage = inject(StorageMap);
    private readonly wsRealTime = inject(WsSymbolService);
    private readonly store = inject(Store);
    private readonly analytics = inject(AnalyticsService);
    private readonly isProduction = inject(SPHERE_ENVIRONMENT_PRODUCTION);

    public state = signalState<SphereRootShellState>({
        collapsedSidenav: false,
        routerNavigationLoading: false,
        displayBottomBar: true,
        uiVersion: 'v2',
        isEnverusAuthentication: false,
        user: null,
        ready: false
    });

    public currentApp = this.navigationService.currentApp;
    public isShell = this.navigationService.isShell;
    public hasSideNavigation = this.navigationService.hasSideNavigation;

    public noPaddingRoute = computed(() => {
        return this.navigationService.activatedRouteData()?.noPadding;
    });

    constructor() {
        const swUpdate = inject(ServiceWorkerUpdateService);
        const wsEvents = inject(WsEventService);
        const router = inject(Router);

        this.appInitializer().then();

        swUpdate.checkForUpdates();

        patchState(this.state, {
            isEnverusAuthentication: this.authorization.isEnverusAuthentication,
            user: this.authentication.currentUser
        });

        wsEvents.init();
        this.wsRealTime.init();
        router.events
            .pipe(
                tap(event => {
                    // TODO: every time we update URL changes, queryParams too, this is being fired, we should NOT rely on this.
                    // checkRouterEvent()

                    // Due to animation delay, the timeout wraps the check function to it is run on the next cycle
                    // of the VM, this way, the value is not changed before the lifecycle hook can perform the next check
                    // thus avoiding a NG0100: ExpressionChangedAfterItHasBeenCheckedError error.
                    setTimeout(() => {
                        this.checkRouterEvent(event);
                    }, 0);
                })
            )
            .subscribe();
        this.notificationSubscriptions.loadAllSubscriptions();
        this.companySettings.loadAllWellKnownCompanySettings();
        this.systemSettings.loadTimezones();

        explicitEffect([this.state.collapsedSidenav], ([collapsedSidenav]) => {
            this.storage.set('portal:collapsedSidenav', collapsedSidenav, { type: 'boolean' }).subscribe();
        });
    }

    ngOnInit(): void {
        this.loadLocalStorageUserSettings();
        this.tableThemeChangeListener();
        this.checkPrivacyPolicyConsent();
        this.registerNotificationEventHandlers();
    }

    async appInitializer() {
        console.debug('initializing authorization service...');
        await this.authorization.init();
        this.analytics.initialize({ trackUserLogInEvent: true }, this.isProduction);

        this.store.dispatch(UserSettingsActions.loadGlobalUserSettingGroup());
        this.store.dispatch(UserSettingsActions.loadNotificationUserSettingGroup());

        patchState(this.state, { ready: true });
    }

    private checkRouterEvent(routerEvent: Event): void {
        if (routerEvent instanceof NavigationStart) {
            patchState(this.state, {
                routerNavigationLoading: true
            });
        }

        if (routerEvent instanceof NavigationEnd || routerEvent instanceof NavigationCancel || routerEvent instanceof NavigationError) {
            patchState(this.state, {
                routerNavigationLoading: false
            });
        }
    }

    async checkPrivacyPolicyConsent(): Promise<void> {
        try {
            const value = await localforage.getItem('privacy-policy');
            if (!value) {
                this.bottomSheet.open(CookieNoticeComponent);
            }
        } catch (err) {
            console.debug(`localForage error ${err}`);
        }
    }

    toggleSidenavState() {
        patchState(this.state, state => ({
            collapsedSidenav: !state.collapsedSidenav
        }));
    }

    setSidenavState(collapsedSidenav: boolean) {
        patchState(this.state, {
            collapsedSidenav
        });
    }

    /**
     * Table theme update listener
     */
    private agGridThemes = inject(AgGridThemeService);
    tableThemeChangeListener() {
        this.agGridThemes.initialize();
    }

    /**
     * From user menu
     */
    private resetUserPasswordService = inject(ResetUserPasswordService);
    openResetPasswordDialog() {
        const tenant = this.authorization.userCompanyCode();
        this.resetUserPasswordService.openResetPasswordDialog(this.authentication.currentUser, tenant);
    }

    async logout() {
        await this.authentication.logout();
    }

    private readonly settingsDialog = inject(SettingsFullscreenDialogService);
    openGlobalSettings() {
        this.settingsDialog.openSettings();
    }

    /**
     * Notification events
     */
    private readonly notificationEventHandler = inject(EventHandlersService);
    private registerNotificationEventHandlers() {
        for (const appDefinition of spherePlatformApps.sphereApps) {
            if (appDefinition.NOTIFICATION_EVENT_HANDLERS) {
                for (const notificationEventHandler of appDefinition.NOTIFICATION_EVENT_HANDLERS) {
                    this.notificationEventHandler.registerHandler(
                        notificationEventHandler.entity,
                        notificationEventHandler.operation,
                        notificationEventHandler.isCustomNotification,
                        notificationEventHandler.correlationOnly,
                        notificationEventHandler.notificationBuilder
                    );
                }
            }
        }
    }

    /**
     * Load local storage user settings
     */
    private readonly renderer = inject(Renderer2);
    private loadLocalStorageUserSettings() {
        this.storage
            .get('portal:collapsedSidenav', { type: 'boolean' })
            .pipe(
                take(1),
                filter(value => value !== undefined)
            )
            .subscribe(collapsedSidenav => {
                patchState(this.state, {
                    collapsedSidenav
                });
            });

        this.storage
            .watch<SphereUIVersion>('portal:sphere-ui-version', { type: 'string', enum: ['v1', 'v2'] })
            .pipe(filter(value => value !== undefined))
            .subscribe(uiVersion => {
                patchState(this.state, {
                    uiVersion
                });
            });

        this.storage.watch('portal:theme', { type: 'string' }).subscribe(theme => {
            this.renderer.removeClass(document.body, 'sphere-theme__light');
            this.renderer.removeClass(document.body, 'sphere-theme__dark');
            let themeClass = `sphere-theme__dark`;
            if (!!theme) {
                themeClass = `sphere-theme__${theme}`;
            }
            this.renderer.addClass(document.body, themeClass);
        });

        this.storage.watch('portal:theme-density', { type: 'string' }).subscribe(density => {
            this.renderer.removeClass(document.body, 'sphere-density__compact');
            this.renderer.removeClass(document.body, 'sphere-density__comfortable');
            let densityClass = `sphere-density__comfortable`;
            if (!!density) {
                densityClass = `sphere-density__${density}`;
            }
            this.renderer.addClass(document.body, densityClass);
        });
    }
}
